import type { CardSlot, CardSlotView } from '@kenocyber/shared';
import { CARD_SLOTS } from '@kenocyber/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { combineCards, equipCard, fetchCards, HuntApiError, unequipCard } from '../api/hunt';

/**
 * **Os três espaços de carta, tal como o servidor os tem** (gate V28H.G).
 *
 * ## Nada de otimista, e o cliente não guarda uma cópia autoritativa
 *
 * A resposta traz os três espaços INTEIROS e é ela que fica. Nenhum espaço é
 * pintado cheio antes de o servidor responder, e nenhuma versão de loadout é
 * incrementada localmente: a versão que o próximo pedido cita é sempre a que
 * veio na última resposta. Um espaço que se pinta e depois volta atrás é pior do
 * que um espaço que esperou 200 ms.
 *
 * ## Uma intenção, uma chave
 *
 * A chave de idempotência nasce com a intenção e é REPETIDA se a mesma intenção
 * for repetida - um duplo clique, um retry após uma rede que caiu. O servidor
 * devolve o mesmo resultado e não escreve segunda vez. É o mesmo desenho de
 * `useJewels`, e pela mesma razão.
 *
 * ## Uma resposta velha nunca sobrescreve uma nova
 *
 * Cada leitura leva um número de sequência. Quando duas leituras estão no ar e a
 * primeira chega depois da segunda - o que a rede faz sem avisar - a primeira é
 * DEITADA FORA. Sem isto, um refetch lento a seguir a um equipar rápido
 * repintaria o ecrã com o estado anterior à mudança.
 *
 * ## Um conflito relê
 *
 * Quando o servidor recusa, o estado autoritativo é lido outra vez em vez de ser
 * adivinhado a partir do erro. Duas abas a equipar no mesmo espaço é um caso
 * normal, e a segunda tem de ver o que a primeira fez - sobretudo depois de
 * `loadout_version_conflict`, onde a versão que o cliente tem é
 * comprovadamente velha.
 */

/** Três espaços vazios: o que se mostra antes da primeira resposta. */
const EMPTY_SLOTS: readonly CardSlotView[] = CARD_SLOTS.map((slot) => ({ slot, card: null }));

export interface CardsState {
  readonly slots: readonly CardSlotView[];
  readonly loadoutVersion: number;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  /** A razão NOMEADA da última recusa, quando o servidor deu uma. */
  readonly failure: string | null;
}

/** O que uma mutação diz a quem a pediu. */
export type CardRunOutcome = { ok: true } | { ok: false; reason: string | null };

/** A intenção pendente, para a chave sobreviver a um segundo clique. */
interface PendingIntent {
  readonly signature: string;
  readonly key: string;
}

function newKey(label: string): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `card-${label}-${Date.now().toString(36)}-${random}`;
}

export function useCards(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<CardsState>({
    slots: EMPTY_SLOTS,
    loadoutVersion: 0,
    loading: false,
    busy: false,
    error: null,
    failure: null,
  });
  const pending = useRef<PendingIntent | null>(null);
  const alive = useRef(true);
  /**
   * O número da leitura mais recente PEDIDA, e o da mais recente APLICADA.
   *
   * Duas leituras no ar não chegam por ordem, e a que chega em último não é
   * necessariamente a mais nova. Comparar os dois números é o que impede uma
   * resposta velha de repintar o ecrã por cima de uma nova.
   */
  const issued = useRef(0);
  const applied = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Relê os três espaços.
   *
   * ## `keepFailure`, e porque ele existe
   *
   * Uma recusa RELÊ - e a releitura corria por cima da própria razão que a
   * provocou, apagando-a antes de o jogador chegar a lê-la. O ecrã piscava uma
   * mensagem e ficava sem explicação nenhuma para uma mudança que não aconteceu.
   *
   * Então quem relê por causa de uma recusa diz-lo, e a razão sobrevive à
   * leitura. Uma leitura normal continua a limpar: abrir a aba outra vez não
   * deve mostrar o erro da sessão anterior.
   */
  const load = useCallback(
    async (options: { keepFailure?: boolean } = {}) => {
      if (!enabled) {
        return;
      }
      issued.current += 1;
      const sequence = issued.current;
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const view = await fetchCards();
        if (!alive.current || sequence < applied.current) {
          return;
        }
        applied.current = sequence;
        setState((current) => ({
          slots: view.slots,
          loadoutVersion: view.loadoutVersion,
          loading: false,
          busy: false,
          error: null,
          failure: options.keepFailure === true ? current.failure : null,
        }));
      } catch {
        if (alive.current && sequence >= applied.current) {
          setState((current) => ({
            ...current,
            loading: false,
            error: 'Não foi possível ler as suas cartas.',
          }));
        }
      }
    },
    [enabled],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Corre uma mutação e adota os espaços que voltarem.
   *
   * A assinatura da intenção é o que decide se uma chave é reutilizada: o mesmo
   * gesto sobre o mesmo espaço com a mesma carta é a MESMA intenção, e um
   * segundo clique não pode virar uma segunda escrita.
   *
   * Enquanto uma mutação está no ar, `busy` é verdadeiro e a interface desativa
   * os botões - por isso um clique repetido nem sequer chega aqui. A chave é a
   * rede de segurança para os casos que a interface não vê: um retry, um
   * duplo-clique que passou entre renders.
   */
  const run = useCallback(
    async (
      signature: string,
      call: (idempotencyKey: string) => Promise<{
        slots: readonly CardSlotView[];
        loadoutVersion: number;
      }>,
    ): Promise<CardRunOutcome> => {
      const reuse = pending.current?.signature === signature ? pending.current.key : null;
      const key = reuse ?? newKey(signature.slice(0, 16));
      pending.current = { signature, key };
      issued.current += 1;
      const sequence = issued.current;
      setState((current) => ({ ...current, busy: true, error: null, failure: null }));
      try {
        const response = await call(key);
        if (alive.current && sequence >= applied.current) {
          applied.current = sequence;
          /* Os três espaços INTEIROS que o servidor devolveu. Nunca um remendo. */
          setState({
            slots: response.slots,
            loadoutVersion: response.loadoutVersion,
            loading: false,
            busy: false,
            error: null,
            failure: null,
          });
        }
        pending.current = null;
        return { ok: true };
      } catch (error) {
        const reason = error instanceof HuntApiError ? error.reason : null;
        if (alive.current) {
          setState((current) => ({ ...current, busy: false, failure: reason }));
        }
        /*
         * Uma recusa RELÊ em vez de adivinhar.
         *
         * O erro diz o que falhou; não diz como está a base agora. Outra aba
         * pode ter equipado no meio, e o que o jogador tem de ver é o estado
         * real - não o anterior com uma mensagem por cima. Depois de
         * `loadout_version_conflict` isto é obrigatório: a versão que o cliente
         * tinha está comprovadamente velha, e uma segunda tentativa com ela
         * seria recusada outra vez.
         */
        await load({ keepFailure: true });
        /*
         * A chave SOBREVIVE à recusa, e é isso que torna um retry seguro: se a
         * resposta se perdeu depois de o servidor ter escrito, repetir a MESMA
         * intenção repete a MESMA chave e o envelope idempotente devolve o mesmo
         * resultado sem escrever segunda vez.
         */
        return { ok: false, reason };
      }
    },
    [load],
  );

  const equip = useCallback(
    (slot: CardSlot, itemInstanceId: string, expectedLoadoutVersion: number) =>
      run(`equip:${slot}:${itemInstanceId}`, (idempotencyKey) =>
        equipCard({ slot, itemInstanceId, expectedLoadoutVersion, idempotencyKey }),
      ),
    [run],
  );

  const unequip = useCallback(
    (slot: CardSlot, expectedLoadoutVersion: number) =>
      run(`unequip:${slot}`, (idempotencyKey) =>
        unequipCard({ slot, expectedLoadoutVersion, idempotencyKey }),
      ),
    [run],
  );

  /**
   * Funde duas cópias, e depois relê.
   *
   * A resposta da fusão descreve o que foi fundido e NÃO traz os espaços: uma
   * carta fundida pode ter estado num deles... exceto que não pode - o gatilho
   * do banco recusa aposentar uma instância equipada. Ainda assim, reler é o que
   * mantém a versão do loadout e a coleção coerentes sem o cliente somar nada.
   */
  const combine = useCallback(
    async (firstInstanceId: string, secondInstanceId: string): Promise<CardRunOutcome> => {
      const signature = `combine:${[firstInstanceId, secondInstanceId].sort().join(':')}`;
      const reuse = pending.current?.signature === signature ? pending.current.key : null;
      const key = reuse ?? newKey('combine');
      pending.current = { signature, key };
      setState((current) => ({ ...current, busy: true, error: null, failure: null }));
      try {
        await combineCards({ firstInstanceId, secondInstanceId, idempotencyKey: key });
        pending.current = null;
        await load({ keepFailure: true });
        return { ok: true };
      } catch (error) {
        const reason = error instanceof HuntApiError ? error.reason : null;
        if (alive.current) {
          setState((current) => ({ ...current, busy: false, failure: reason }));
        }
        await load({ keepFailure: true });
        return { ok: false, reason };
      }
    },
    [load],
  );

  return { state, load, equip, unequip, combine };
}
